import { useState } from "react";
import { FileText, GitCommitVertical, PenLine } from "lucide-react";
import { FlowDiagram, TechTag, FadeIn, slug } from "./common";
import { CategoryBackground } from "./SciBackground";
import CategoryHeader from "./CategoryHeader";

const DOC_TYPES = [
  ["SOP", "Standard Operating Procedure"],
  ["WI", "Work Instruction"],
  ["DEV", "Deviation / Investigation Record"],
  ["CAPA", "Corrective and Preventive Action"],
  ["ECO", "Engineering Change Order / Change Documentation"],
  ["CHANGE CONTROL", "Controlled process / system change"],
  ["VALIDATION", "Protocol / acceptance criteria / technical report"],
  ["RISK", "FMEA / risk assessment"],
  ["TRAINING", "Training impact / implementation evidence"],
];

const LIFECYCLE = [
  {
    n: "01", t: "Process / Quality Need",
    d: "Triggered by a deviation, nonconformance, CAPA, process improvement, engineering change, validation activity, audit finding, new process or specification change.",
    c: "Connects the technical trigger to the documentation impact — what must change, in which controlled documents.",
  },
  {
    n: "02", t: "Technical Assessment",
    d: "Define the intended change, the affected process, affected records and downstream impact.",
    c: "Performs the process and data assessment so the document change is technically justified, not cosmetic.",
  },
  {
    n: "03", t: "Risk Assessment",
    d: "Evaluate severity, occurrence and detectability; assess change-control impact and training implications.",
    c: "Frames the risk picture so documentation effort is proportionate to product and process risk.",
  },
  {
    n: "04", t: "Draft",
    d: "Translate technical and process requirements into clear controlled instructions.",
    c: "Authors SOP/WI content that reflects how the process actually runs — precise enough to execute, clear enough to train.",
  },
  {
    n: "05", t: "SME Review",
    d: "Coordinate review with Manufacturing, Engineering, Product Quality, analytical teams and technical SMEs.",
    c: "Consolidates SME input and resolves conflicts between what is written and what is practised.",
  },
  {
    n: "06", t: "Quality Review",
    d: "Confirm compliance, traceability, consistency and revision-control requirements.",
    c: "Prepares the document for Quality review with rationale, evidence links and GDP/ALCOA+ expectations in mind.",
  },
  {
    n: "07", t: "Approval",
    d: "Controlled approval workflow before anything becomes effective.",
    c: "Supports the approval package so reviewers can approve on objective evidence, not on faith.",
  },
  {
    n: "08", t: "Training Impact",
    d: "Assess who must be trained, on what, before the effective date.",
    c: "Defines the training-impact assessment and verifies the floor can execute the revised instruction.",
  },
  {
    n: "09", t: "Controlled Implementation",
    d: "The change is implemented under control — no uncontrolled copies, no informal workarounds.",
    c: "Coordinates effective-date implementation so practice and documentation change together.",
  },
  {
    n: "10", t: "Effective Version",
    d: "The current controlled version becomes operational.",
    c: "Confirms the effective version is the version actually in use.",
  },
  {
    n: "11", t: "Effectiveness Monitoring",
    d: "Verify the documented change performs as intended in operation.",
    c: "Watches the process-performance metrics the change was meant to move.",
  },
  {
    n: "12", t: "Periodic Review",
    d: "Evaluate whether documentation still reflects the actual process.",
    c: "Uses process performance data to flag documents drifting away from reality.",
  },
  {
    n: "13", t: "Supersession / Retirement",
    d: "Maintain traceable historical versions; retire documents under control.",
    c: "Preserves the revision trail so any historical decision remains reconstructable and audit-ready.",
  },
];

const PRINCIPLES = [
  ["Clear", "Users must understand what to do."],
  ["Technically Accurate", "Instructions must reflect the actual process."],
  ["Traceable", "Change rationale and history should be recoverable."],
  ["Controlled", "Only approved, current versions should be in use."],
  ["Risk-Based", "Documentation effort reflects process and product risk."],
  ["Implementable", "The process described must work on the manufacturing floor."],
  ["Audit Ready", "Records demonstrate what changed, why and how it was implemented."],
];

const BA_QMS_FLOW = [
  {
    title: "Business Analysis × Product Quality × QMS × Digital Transformation",
    items: [
      { label: "Process Problem", detail: "An operational or Product Quality issue observed in manufacturing." },
      { label: "Requirements", detail: "What the improved process must achieve — captured before any solution." },
      { label: "Technical Analysis", detail: "Data, RCA and statistical evidence define the real problem." },
      { label: "Risk", detail: "Impact and risk assessment shapes the proportionate response." },
      { label: "Solution", detail: "The technical, process or digital change." },
      { label: "Controlled Documentation", detail: "SOP/WI, ECO and change records make the solution repeatable and compliant." },
      { label: "Implementation", detail: "The documented change goes live under control." },
      { label: "Training", detail: "People execute the new process correctly." },
      { label: "Effectiveness", detail: "Monitoring confirms the improvement holds." },
    ],
  },
];

export default function QmsSection() {
  const [stage, setStage] = useState(2);
  return (
    <section id="qms" className="relative overflow-hidden scroll-mt-16" data-testid="qms-section">
      <CategoryBackground variant="lifecycle" accent="violet" accent2="eblue" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <CategoryHeader
          id="qms"
          category="qms"
          eyebrow="QMS Documentation & Controlled Change"
          title="Translating technical findings into controlled Quality-system documentation"
          description="Controlled documentation, SOP/WI development, deviations, CAPA, ECO/change lifecycle and implementation support — the lifecycle that turns an investigation result into a controlled, trained, effective way of working."
        />
        <FadeIn>
          <p className="text-pale/85 text-base leading-relaxed max-w-3xl mt-6" data-testid="qms-positioning">
            Poojan has experience translating manufacturing investigations, technical findings and process changes into controlled Quality
            documentation — drafting and revising SOPs and Work Instructions, supporting deviation, CAPA and ECO/change documentation, preparing
            technical reports and validation records, aligning training requirements and maintaining revision-control and GDP/ALCOA+ expectations.
            This is not administrative paperwork: it is technical writing plus process understanding plus Quality-system execution.
          </p>
        </FadeIn>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-2 mt-10" data-testid="qms-doc-types">
          {DOC_TYPES.map(([abbr, full], i) => (
            <FadeIn key={abbr} delay={i * 0.04}>
              <div className="panel p-3 h-full text-center hover:border-cyan/40 transition-colors" data-testid={`qms-doc-${slug(abbr)}`}>
                <FileText className="w-4 h-4 text-cyan mx-auto" aria-hidden="true" />
                <p className="font-mono text-[11px] text-cyan mt-2">{abbr}</p>
                <p className="text-[10px] text-steel mt-1 leading-snug">{full}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        <div className="mt-16">
          <h3 className="font-sans tracking-tight text-2xl text-paper">The Controlled-Document Lifecycle</h3>
          <p className="text-steel text-sm mt-2">Each stage is clickable — what Poojan contributes at that stage.</p>
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6 mt-8">
            <div className="space-y-1" data-testid="qms-lifecycle">
              {LIFECYCLE.map((s, i) => (
                <div key={s.n}>
                  <button
                    data-testid={`qms-stage-${s.n}`}
                    onClick={() => setStage(i)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-sm border text-left transition-colors ${
                      stage === i ? "border-cyan bg-cyan/10" : "border-transparent hover:border-teal/40"
                    }`}
                  >
                    <span className={`font-mono text-xs w-6 ${stage === i ? "text-cyan" : "text-steel"}`}>{s.n}</span>
                    <span className={`font-sans text-sm font-semibold tracking-wide ${stage === i ? "text-paper" : "text-pale/70"}`}>{s.t}</span>
                  </button>
                  {i < LIFECYCLE.length - 1 && <div className="ml-7 h-2.5 w-px bg-teal/40" aria-hidden="true" />}
                </div>
              ))}
            </div>
            <div className="panel p-6 self-start lg:sticky lg:top-20" data-testid="qms-stage-detail">
              <p className="font-mono text-cyan text-xs">{LIFECYCLE[stage].n}</p>
              <h4 className="font-sans text-xl font-semibold text-paper mt-2">{LIFECYCLE[stage].t}</h4>
              <p className="text-sm text-pale/85 mt-3 leading-relaxed">{LIFECYCLE[stage].d}</p>
              <div className="mt-5 pt-4 border-t border-cyan/10">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel mb-2 flex items-center gap-1.5">
                  <PenLine className="w-3 h-3 text-cyan" aria-hidden="true" /> Poojan's contribution
                </p>
                <p className="text-sm text-cyan/90 font-body leading-relaxed">{LIFECYCLE[stage].c}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16">
          <h3 className="font-sans tracking-tight text-2xl text-paper">Documentation Methodology</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-7 gap-2 mt-8" data-testid="qms-principles">
            {PRINCIPLES.map(([t, d], i) => (
              <FadeIn key={t} delay={i * 0.04}>
                <div className="panel p-4 h-full" data-testid={`qms-principle-${slug(t)}`}>
                  <p className="font-sans text-sm font-semibold text-cyan">{t}</p>
                  <p className="text-[11px] text-steel mt-2 leading-snug">{d}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>

        <div className="mt-16 grid lg:grid-cols-[1.2fr_1fr] gap-8 items-start">
          <div>
            <h3 className="font-sans tracking-tight text-2xl text-paper mb-6">Business Analysis × Product Quality × QMS × Digital Transformation</h3>
            <FlowDiagram layers={BA_QMS_FLOW} testId="ba-qms-flow" />
          </div>
          <div className="panel p-6 border-l-2 border-l-cyan lg:sticky lg:top-20" data-testid="qms-principle-statement">
            <GitCommitVertical className="w-5 h-5 text-cyan" aria-hidden="true" />
            <p className="font-sans text-lg text-paper mt-3 leading-relaxed">
              Quality-system documentation converts technical findings into repeatable operating control.
            </p>
            <p className="text-sm text-steel mt-4 leading-relaxed">
              An investigation that ends in a slide deck changes nothing. The same finding, carried through deviation, CAPA, ECO and revised SOP/WI
              with training and effectiveness monitoring, becomes the way the process actually runs.
            </p>
            <div className="flex flex-wrap gap-1.5 mt-5">
              {["SOP Development", "Work Instructions", "Technical Writing", "ECO", "Deviation", "CAPA", "Change Control", "Validation Documentation", "Document Lifecycle", "Revision Control", "ALCOA+", "GDP"].map((t) => (
                <TechTag key={t}>{t}</TechTag>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
